import type { StatamicAssets, StatamicAsset } from "@customTypes/statamic";
import type { EventEntry } from "../types/entries";

/**
 * CDN utilities
 * -----------------------------------------------------------------------------
 */

/**
 * Convert a Statamic asset to a CDN URL
 * @param imageUrl - The URL of the image to convert
 * @param options - The options to convert the image to a CDN URL
 * @returns The CDN URL of the image with any optional parameters specific to BunnyCDN
 * @see https://docs.bunny.net/docs/stream-image-processing
 */

export function convertStatamicAssetToCDN(
  imageUrl?: string | null,
  options?: {
    aspect_ratio?: "1:1" | "4:3" | "16:9" | "2:4";
    width?: number;
    quality?: number;
    blur?: number;
    crop?: number[];
  },
) {
  if (!imageUrl) return;

  /**
   * TODO: find a better way to handle subsplash background blur.
   * At the moment we just want to prevent them from loading as background.
   */
  if (imageUrl.includes("subsplash.com")) return;

  let optionsQueryString = ``;
  if (options) {
    optionsQueryString = "?";
    if (options.aspect_ratio) {
      optionsQueryString += `&aspect_ratio=${options.aspect_ratio}`;
    }
    if (options.quality) {
      optionsQueryString += `&quality=${options.quality}`;
    }
    if (options.width) {
      optionsQueryString += `&width=${options.width}`;
    }
    if (options.blur) {
      optionsQueryString += `&blur=${options.blur}`;
    }
    if (options.crop) {
      optionsQueryString += `&crop=${options.crop[0]},${options.crop[1]}`;
    }
    optionsQueryString = optionsQueryString.replace("?&", "?");
  }

  return `https://b-cdn.wildatheart.org/${imageUrl.replace("s3::", "").replace("https://b-cdn.wildatheart.org/", "")}${optionsQueryString}`;
}

function getHash(text: string): number {
  if (!text) {
    return 0;
  }
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash << 5) - hash + text.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash;
}

export function getImageFromText(text: string, images: string[]): string {
  // Create a simple hash of the text string
  const hash = getHash(text);

  // Get positive index within array bounds
  const index = Math.abs(hash) % images.length;

  return images[index];
}

export function getCropValuesFromText(text: string): number[] {
  const hash = getHash(text);
  const cropValues = [
    [100, 200],
    [300, 150],
    [250, 400],
    [150, 300],
  ];
  const cropIndex = Math.abs(hash) % cropValues.length;

  return cropValues[cropIndex];
}

export function deriveBlurredBackgroundImageURLFromText(
  text: string,
  images: string[],
  blur: number = 100,
): string | undefined {
  const image = getImageFromText(text, images);
  const cropValues = getCropValuesFromText(text);

  return convertStatamicAssetToCDN(image, {
    blur,
    crop: cropValues,
  });
}
